"use client";

import React from "react";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
import { Utensils, Wine, Award, Flame } from "lucide-react";
const chefs = [{
  name: "Erik Sørensen",
  role: "Executive Chef",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1602682/img/chef_erik_portrait.png",
  philosophy: "Nature is the ultimate artist; we are merely its curators of flavor.",
  training: "Staged at Noma and Geranium; 15 years leading Copenhagen's elite kitchens.",
  signature: "Smoked Langoustine with Pine Oil and Fermented Gooseberry",
  icon: <Utensils className="size-5" />
}, {
  name: "Freja Nilsson",
  role: "Head Sommelier",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1602682/img/sommelier_freja_portrait.png",
  philosophy: "The best pairings are found in the wild, mirroring the terroir of the North.",
  training: "Court of Master Sommeliers; specialized in biodynamic Nordic vintages.",
  signature: "Birch Sap & Wild Apple Ferment (2021 Reserve)",
  icon: <Wine className="size-5" />
}, {
  name: "Lars Bak",
  role: "Sous Chef",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1602682/img/chef_lars_portrait.png",
  philosophy: "Precision is the bridge between a raw ingredient and a piece of edible art.",
  training: "Mentored at Frantzén; focused on open-fire techniques and curing.",
  signature: "Cured Venison with Reindeer Moss and Juniper Emulsion",
  icon: <Flame className="size-5" />
}, {
  name: "Astrid Jensen",
  role: "Pastry Chef",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1602682/img/chef_astrid_portrait.png",
  philosophy: "Sweetness should be a whisper, not a shout, balanced by salt and soil.",
  training: "Pastry Arts Academy of Paris; pioneer of the 'Savory Dessert' movement.",
  signature: "Sea Buckthorn Meringue with Licorice Root and Salted Skyr",
  icon: <Award className="size-5" />
}];
export default function MeetTheKitchenTeam() {
  const {
    ref,
    isVisible
  } = useRevealOnScroll();
  return <section data-section-id="39652" id="masters-of-presentation" ref={ref} className={`dark bg-background text-foreground py-20 px-4 md:px-8 transition-opacity duration-1000 ${isVisible ? "opacity-100" : "opacity-0"}`}>
      <div className="max-w-7xl mx-auto">
        <div className="text-center mb-16 space-y-4">
          <h2 className="text-4xl md:text-6xl font-serif font-light tracking-tighter text-foreground">
            Masters of Presentation
          </h2>
          <p className="text-muted-foreground max-w-2xl mx-auto text-lg font-light tracking-wide">
            The architects of NordicBite. A collective of visionaries redefining the boundaries of contemporary Scandinavian gastronomy.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
          {chefs.map((chef, index) => <div key={index} data-index={index} className="group perspective-1000">
              <Card className="h-full border-border bg-card shadow-none overflow-hidden hover:shadow-2xl hover:shadow-black/40 transition-all duration-700 hover:-translate-y-2 rounded-sm">
                {}
                <div className="relative h-80 w-full overflow-hidden">
                  <img src={chef.image} alt={`${chef.name} portrait`} className="h-full w-full object-cover transition-all duration-1000 group-hover:scale-105 grayscale group-hover:grayscale-0" />
                  {}
                  <div className="absolute inset-0 bg-primary/90 flex flex-col items-center justify-center p-6 opacity-0 group-hover:opacity-100 transition-opacity duration-500 backdrop-blur-sm">
                    <p className="text-primary-foreground text-center font-serif text-xl italic font-light leading-relaxed mb-4">
                      &ldquo;{chef.philosophy}&rdquo;
                    </p>
                    <p className="text-primary-foreground/70 font-serif text-2xl tracking-widest opacity-50 select-none">
                      {chef.name.split(' ').pop()}
                    </p>
                  </div>
                </div>

                {}
                <CardHeader className="pb-2">
                  <div className="flex items-center justify-between">
                    <CardTitle className="text-lg font-serif font-light tracking-tight text-foreground">
                      {chef.name}
                    </CardTitle>
                    <div className="text-primary opacity-60">
                      {chef.icon}
                    </div>
                  </div>
                  <CardDescription className="text-primary font-mono uppercase tracking-[0.2em] text-[10px]">
                    {chef.role}
                  </CardDescription>
                </CardHeader>

                <CardContent>
                  <Accordion type="single" collapsible className="w-full">
                    <AccordionItem value="details" className="border-border">
                      <AccordionTrigger className="text-[10px] font-mono uppercase tracking-[0.1em] text-muted-foreground hover:text-primary py-3">
                        Curriculum Vitae
                      </AccordionTrigger>
                      <AccordionContent className="space-y-4 pt-2">
                        <div>
                          <h4 className="text-[9px] font-mono font-bold text-primary uppercase tracking-[0.2em] mb-1">
                            Background
                          </h4>
                          <p className="text-sm text-muted-foreground leading-relaxed font-light">
                            {chef.training}
                          </p>
                        </div>
                        <div>
                          <h4 className="text-[9px] font-mono font-bold text-primary uppercase tracking-[0.2em] mb-1">
                            Innovation
                          </h4>
                          <p className="text-sm text-muted-foreground leading-relaxed font-light italic">
                            {chef.signature}
                          </p>
                        </div>
                      </AccordionContent>
                    </AccordionItem>
                  </Accordion>
                </CardContent>
              </Card>
            </div>)}
        </div>
      </div>
    </section>;
}